import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  AlertCircle,
  Info,
  Pill,
  Stethoscope,
  ClipboardList,
  AlertTriangle,
  Activity,
  GitBranch,
  Repeat,
  Beaker,
  ChevronUp,
  ChevronDown,
  FileText,
  CheckCircle2,
  ExternalLink,
  X,
  Loader2,
} from 'lucide-react';

// UI Components
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../components/ui/accordion';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import PatientEducation from '../../DrugSearch/DrugInformation/components/PatientEducation/PatientEducation';
import EmptyState from '../../../components/EmptyState/EmptyState';
import SeverityIndicator from '../../../components/SeverityIndicator/SeverityIndicator';

// Services & Utils
import drugsApiService from '../../../services/drugsapi.service';
import { mapDrugInfoResponse } from '../../../utils/drugInfoMapper';
import { 
  mapPregnancyMonograph, 
  mapDrugDrugMonograph, 
  mapDrugFoodMonograph 
} from '../../../utils/monographMapper';

/**
 * DrugDetailsTab Component
 * Tab that displays detailed drug information from FDB API
 * Replaces the modal approach for better UX integration
 */
const DrugDetailsTab = ({ drugMedId, drugName }) => {
  // Drug data state
  const [drugData, setDrugData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Side Effects Search and Sorting
  const [sideEffectSearchQuery, setSideEffectSearchQuery] = useState('');
  const [sideEffectSortColumn, setSideEffectSortColumn] = useState(null);
  const [sideEffectSortDirection, setSideEffectSortDirection] = useState(null);

  // Interactions Search and Sorting
  const [interactionSearchQuery, setInteractionSearchQuery] = useState('');
  const [interactionSortColumn, setInteractionSortColumn] = useState(null);
  const [interactionSortDirection, setInteractionSortDirection] = useState(null);

  // Precautions tab state
  const [activePrecautionTab, setActivePrecautionTab] = useState('warnings');

  // Universal monograph modal state
  const [monographDialogOpen, setMonographDialogOpen] = useState(false);
  const [activeMonograph, setActiveMonograph] = useState(null);
  const [monographLoading, setMonographLoading] = useState(false);
  const [monographTitle, setMonographTitle] = useState('');

  // Fetch drug data when drugMedId changes
  useEffect(() => {
    if (drugMedId) {
      fetchDrugInfo();
    } else {
      setDrugData(null);
      setError(null);
    }
  }, [drugMedId]);

  const fetchDrugInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      setDrugData(null);

      const response = await drugsApiService.getDrugInfo(drugMedId);

      if (!response) {
        throw new Error('Failed to fetch drug information');
      }

      const mappedData = mapDrugInfoResponse(response);

      if (!mappedData) {
        throw new Error('Invalid drug data received');
      }

      setDrugData(mappedData);
    } catch (err) {
      console.error('Error fetching drug info:', err);
      setError(err.message || 'Failed to load drug information');
      setDrugData(null);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions to organize precautions by category
  const getPrecautionsByGroup = (group) => {
    return drugData?.precautions?.filter((p) => p.group === group) || [];
  };

  const getLabelWarnings = () => {
    return drugData?.precautions?.filter((p) => p.group === 'Label Warning') || [];
  };

  // Universal monograph handler
  const handleViewMonograph = async (type, monographId) => {
    if (!monographId) return;

    setMonographLoading(true);
    setActiveMonograph(null);
    setMonographDialogOpen(true);

    try {
      let response;
      let mappedMonograph;

      if (type === 'pregnancy') {
        setMonographTitle('Pregnancy Precautions Monograph');
        response = await drugsApiService.getPregnancyMonograph(monographId.toString());
        mappedMonograph = response ? mapPregnancyMonograph(response) : null;
      } else if (type === 'drugFood') {
        setMonographTitle('Drug-Food Interaction Monograph');
        response = await drugsApiService.getDrugFoodMonograph(Number(monographId));
        mappedMonograph = response ? mapDrugFoodMonograph(response) : null;
      } else if (type === 'drugDrug') {
        setMonographTitle('Drug-Drug Interaction Monograph');
        response = await drugsApiService.getDrugMonograph(Number(monographId));
        mappedMonograph = response ? mapDrugDrugMonograph(response) : null;
      }

      if (!mappedMonograph) {
        throw new Error('Failed to load monograph');
      }

      setActiveMonograph(mappedMonograph);
    } catch (error) {
      console.error('Error fetching monograph:', error);
      setActiveMonograph(null);
    } finally {
      setMonographLoading(false);
    }
  };

  // Handle side effects sorting
  const handleSideEffectSort = (column) => {
    if (sideEffectSortColumn === column) {
      if (sideEffectSortDirection === 'asc') setSideEffectSortDirection('desc');
      else if (sideEffectSortDirection === 'desc') {
        setSideEffectSortColumn(null);
        setSideEffectSortDirection(null);
      }
    } else {
      setSideEffectSortColumn(column);
      setSideEffectSortDirection('asc');
    }
  };

  // Filter side effects
  const filteredSideEffects = drugData?.sideEffects?.filter((effect) => {
    const matchesSearch =
      !sideEffectSearchQuery ||
      effect.name.toLowerCase().includes(sideEffectSearchQuery.toLowerCase());
    return matchesSearch;
  }) || [];

  // Sort side effects
  const sortedSideEffects = useMemo(() => {
    if (!sideEffectSortColumn || !sideEffectSortDirection) return filteredSideEffects;
    
    return [...filteredSideEffects].sort((a, b) => {
      let compareA;
      let compareB;
      
      switch (sideEffectSortColumn) {
        case 'name':
          compareA = a.name.toLowerCase();
          compareB = b.name.toLowerCase();
          break;
        case 'frequency':
          const frequencyOrder = { 'more frequent': 0, 'less frequent': 1, 'rare/very rare': 2 };
          compareA = frequencyOrder[a.frequency] ?? 3;
          compareB = frequencyOrder[b.frequency] ?? 3;
          break;
        case 'severity':
          const severityOrder = { 'Severe': 0, 'Less Severe': 1 };
          compareA = severityOrder[a.severity] ?? 2;
          compareB = severityOrder[b.severity] ?? 2;
          break;
        default:
          return 0;
      }
      
      if (compareA < compareB) return sideEffectSortDirection === 'asc' ? -1 : 1;
      if (compareA > compareB) return sideEffectSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredSideEffects, sideEffectSortColumn, sideEffectSortDirection]);

  // Handle interactions sorting
  const handleInteractionSort = (column) => {
    if (interactionSortColumn === column) {
      if (interactionSortDirection === 'asc') setInteractionSortDirection('desc');
      else if (interactionSortDirection === 'desc') {
        setInteractionSortColumn(null);
        setInteractionSortDirection(null);
      }
    } else {
      setInteractionSortColumn(column);
      setInteractionSortDirection('asc');
    }
  };

  // Filter interactions
  const filteredInteractions = drugData?.interactions?.filter((interaction) => {
    const matchesSearch =
      !interactionSearchQuery ||
      interaction.with.toLowerCase().includes(interactionSearchQuery.toLowerCase()) ||
      interaction.clinicalEffect.toLowerCase().includes(interactionSearchQuery.toLowerCase());
    return matchesSearch;
  }) || [];

  // Sort interactions
  const sortedInteractions = useMemo(() => {
    if (!interactionSortColumn || !interactionSortDirection) return filteredInteractions;
    
    return [...filteredInteractions].sort((a, b) => {
      let compareA;
      let compareB;
      
      switch (interactionSortColumn) {
        case 'type':
          compareA = a.type.toLowerCase();
          compareB = b.type.toLowerCase();
          break;
        case 'with':
          compareA = a.with.toLowerCase();
          compareB = b.with.toLowerCase();
          break;
        case 'clinicalEffect':
          compareA = a.clinicalEffect.toLowerCase();
          compareB = b.clinicalEffect.toLowerCase();
          break;
        case 'severity':
          const getSeverityOrder = (severity) => {
            const normalized = (severity || '').toLowerCase().trim();
            if (normalized.includes('contraindicated') || normalized.includes('contraindication')) return 0;
            if (normalized.includes('severe') || normalized.includes('known interaction')) return 1;
            if (normalized.includes('moderate')) return 2;
            if (normalized.includes('minor')) return 3;
            return 4;
          };
          compareA = getSeverityOrder(a.severity);
          compareB = getSeverityOrder(b.severity);
          break;
        default:
          return 0;
      }
      
      if (compareA < compareB) return interactionSortDirection === 'asc' ? -1 : 1;
      if (compareA > compareB) return interactionSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredInteractions, interactionSortColumn, interactionSortDirection]);

  // Sort icon helper
  const SortIcon = ({ column, currentColumn, direction }) => {
    if (currentColumn === column) {
      return direction === 'asc' ? (
        <ChevronUp className="h-4 w-4" />
      ) : (
        <ChevronDown className="h-4 w-4" />
      );
    }
    return (
      <div className="h-4 w-4 opacity-30">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M7 15l5 5 5-5M7 9l5-5 5 5" />
        </svg>
      </div>
    );
  };

  // No drugMedId available
  if (!drugMedId) {
    return (
      <Card className="shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <Pill className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Drug Details Not Available
            </h3>
            <p className="text-sm text-muted-foreground max-w-md">
              Detailed drug information is not available for this product. 
              This typically occurs for bulk chemicals or compounds without an FDB drug ID.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <Loader2 className="h-12 w-12 text-primary animate-spin mb-4" />
            <p className="text-base font-medium text-foreground">
              Loading drug information...
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="shadow-sm">
        <CardContent className="pt-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-sm leading-relaxed">
              {error}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // No data
  if (!drugData) {
    return null;
  }

  return (
    <>
      <div className="space-y-4">
        {/* Header */}
        <div>
          <h3 className="text-lg font-semibold">Drug Details</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Comprehensive drug information from First Databank
          </p>
        </div>

        {/* Drug Information Accordions */}
        <Accordion
          type="multiple"
          defaultValue={['general', 'clinical']}
          className="space-y-3 sm:space-y-4"
        >
          {/* General Information */}
          <AccordionItem value="general" className="border rounded-lg bg-card" id="general">
            <AccordionTrigger className="px-4 sm:px-6 hover:no-underline hover:bg-muted/50 min-h-[56px]">
              <div className="flex items-center gap-2 sm:gap-3">
                <Info className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" />
                <span className="font-semibold text-base sm:text-lg">
                  General Information
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                <div>
                  <span className="text-sm font-semibold text-muted-foreground">
                    Drug Name
                  </span>
                  <p className="text-base mt-1">{drugData.name}</p>
                </div>
                <div>
                  <span className="text-sm font-semibold text-muted-foreground">
                    Generic Name
                  </span>
                  {drugData.genericName ? (
                    <p className="text-base mt-1 capitalize">
                      {drugData.genericName}
                    </p>
                  ) : (
                    <div className="mt-1">
                      <EmptyState message="Generic name not provided by manufacturer" />
                    </div>
                  )}
                </div>
                <div>
                  <span className="text-sm font-semibold text-muted-foreground">
                    Drug Type
                  </span>
                  <p className="text-base mt-1">{drugData.type || 'Generic'}</p>
                </div>
                <div>
                  <span className="text-sm font-semibold text-muted-foreground">
                    Status
                  </span>
                  <p className="text-base mt-1">{drugData.status || 'Active'}</p>
                </div>
                <div>
                  <span className="text-sm font-semibold text-muted-foreground">
                    Federal Legend
                  </span>
                  <p className="text-base mt-1">{drugData.federalLegend || 'Prescription Required'}</p>
                </div>
                <div className="md:col-span-2">
                  <span className="text-sm font-semibold text-muted-foreground">
                    Description
                  </span>
                  {drugData.description ? (
                    <p className="text-sm mt-1 text-muted-foreground">
                      {drugData.description}
                    </p>
                  ) : (
                    <div className="mt-1">
                      <EmptyState message="Description not provided by manufacturer" />
                    </div>
                  )}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Clinical Details */}
          <AccordionItem value="clinical" className="border rounded-lg bg-card" id="clinical">
            <AccordionTrigger className="px-4 sm:px-6 hover:no-underline hover:bg-muted/50 min-h-[56px]">
              <div className="flex items-center gap-2 sm:gap-3">
                <Stethoscope className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" />
                <span className="font-semibold text-base sm:text-lg">
                  Clinical Details
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                <div>
                  <span className="text-sm font-semibold text-muted-foreground">
                    Route
                  </span>
                  {drugData.route ? (
                    <p className="text-base mt-1 capitalize">{drugData.route}</p>
                  ) : (
                    <div className="mt-1">
                      <EmptyState message="Administration route not documented in source" />
                    </div>
                  )}
                </div>
                <div>
                  <span className="text-sm font-semibold text-muted-foreground">
                    Dosage Form
                  </span>
                  {drugData.dosageForm ? (
                    <p className="text-base mt-1 capitalize">{drugData.dosageForm}</p>
                  ) : (
                    <div className="mt-1">
                      <EmptyState message="Dosage form not provided by manufacturer" />
                    </div>
                  )}
                </div>
                <div>
                  <span className="text-sm font-semibold text-muted-foreground">
                    Strength
                  </span>
                  {drugData.strength ? (
                    <p className="text-base mt-1">{drugData.strength}</p>
                  ) : (
                    <div className="mt-1">
                      <EmptyState message="Strength information not provided" />
                    </div>
                  )}
                </div>
                <div className="md:col-span-3">
                  <span className="text-sm font-semibold text-muted-foreground">
                    Classification
                  </span>
                  {drugData.classification ? (
                    <p className="text-base mt-1">{drugData.classification}</p>
                  ) : (
                    <div className="mt-1">
                      <EmptyState message="Classification information not available in database" />
                    </div>
                  )}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Indications */}
          <AccordionItem value="indications" className="border rounded-lg bg-card" id="indications">
            <AccordionTrigger className="px-4 sm:px-6 hover:no-underline hover:bg-muted/50 min-h-[56px]">
              <div className="flex items-center gap-2 sm:gap-3">
                <ClipboardList className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" />
                <span className="font-semibold text-base sm:text-lg">
                  Indications
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
              <div className="space-y-2 mt-2">
                {drugData.indications?.length > 0 ? (
                  drugData.indications.map((indication, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                      <span>{indication.condition}</span>
                    </div>
                  ))
                ) : (
                  <EmptyState message="No approved indications documented in our database" />
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Precautions & Risks */}
          <AccordionItem value="precautions" className="border rounded-lg bg-card" id="precautions">
            <AccordionTrigger className="px-4 sm:px-6 hover:no-underline hover:bg-muted/50 min-h-[56px]">
              <div className="flex items-center gap-2 sm:gap-3">
                <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" />
                <span className="font-semibold text-base sm:text-lg">
                  Precautions & Risks
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
              <Tabs
                value={activePrecautionTab}
                onValueChange={setActivePrecautionTab}
                className="mt-2"
              >
                <TabsList className="grid w-full grid-cols-5 mb-4">
                  <TabsTrigger value="warnings">Warnings</TabsTrigger>
                  <TabsTrigger value="pregnancy">Pregnancy</TabsTrigger>
                  <TabsTrigger value="lactation">Lactation</TabsTrigger>
                  <TabsTrigger value="pediatric">Pediatric</TabsTrigger>
                  <TabsTrigger value="geriatric">Geriatric</TabsTrigger>
                </TabsList>

                {/* Warnings Tab */}
                <TabsContent value="warnings" className="space-y-3">
                  {getLabelWarnings().length > 0 ? (
                    getLabelWarnings().map((warning, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <Info className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                        <p className="text-sm flex-1">{warning.text}</p>
                      </div>
                    ))
                  ) : (
                    <EmptyState message="No label warnings documented in our database" />
                  )}
                </TabsContent>

                {/* Pregnancy Tab */}
                <TabsContent value="pregnancy" className="space-y-4">
                  {getPrecautionsByGroup('Pregnancy').length > 0 ? (
                    getPrecautionsByGroup('Pregnancy').map((precaution, idx) => (
                      <div key={idx} className="space-y-3">
                        <SeverityIndicator severity={precaution.severity} size="sm" className="mb-2" />
                        <p className="text-sm">{precaution.text}</p>
                        {precaution.monographId && (
                          <button 
                            onClick={() => handleViewMonograph('pregnancy', precaution.monographId)}
                            className="flex items-center gap-1 text-sm text-primary hover:underline"
                          >
                            <ExternalLink className="h-3 w-3" />
                            View Details
                          </button>
                        )}
                      </div>
                    ))
                  ) : (
                    <EmptyState message="No pregnancy-specific precautions documented" />
                  )}
                </TabsContent>

                {/* Lactation Tab */}
                <TabsContent value="lactation" className="space-y-4">
                  {getPrecautionsByGroup('Lactation').length > 0 ? (
                    getPrecautionsByGroup('Lactation').map((precaution, idx) => (
                      <div key={idx} className="space-y-2">
                        <SeverityIndicator severity={precaution.severity} size="sm" />
                        <p className="text-sm">{precaution.text}</p>
                      </div>
                    ))
                  ) : (
                    <EmptyState message="No lactation-specific precautions documented" />
                  )}
                </TabsContent>

                {/* Pediatric Tab */}
                <TabsContent value="pediatric" className="space-y-4">
                  {getPrecautionsByGroup('Pediatric').length > 0 ? (
                    getPrecautionsByGroup('Pediatric').map((precaution, idx) => (
                      <div key={idx} className="space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium">{precaution.severity}</span>
                          {precaution.ageRange && (
                            <Badge variant="outline" className="text-xs px-2 py-0.5">
                              {precaution.ageRange}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm">{precaution.text}</p>
                      </div>
                    ))
                  ) : (
                    <EmptyState message="No pediatric-specific precautions documented" />
                  )}
                </TabsContent>

                {/* Geriatric Tab */}
                <TabsContent value="geriatric" className="space-y-4">
                  {getPrecautionsByGroup('Geriatric').length > 0 ? (
                    getPrecautionsByGroup('Geriatric').map((precaution, idx) => {
                      const drugNameDisplay = precaution.drugName?.split(' ')[0] || 
                                      drugData?.genericName?.split(' ')[0] || 
                                      'Drug';
                      
                      return (
                        <div key={idx} className="space-y-2">
                          <h4 className="font-semibold text-sm">{drugNameDisplay}</h4>
                          <p className="text-sm font-medium">{precaution.severity}</p>
                          <p className="text-sm">{precaution.text}</p>
                          <div className="flex gap-2 flex-wrap mt-2">
                            {precaution.hasHepaticRisk && (
                              <Badge variant="outline" className="text-xs px-2 py-0.5">
                                Hepatic Risk
                              </Badge>
                            )}
                            {precaution.hasRenalRisk && (
                              <Badge variant="outline" className="text-xs px-2 py-0.5">
                                Renal Risk
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <EmptyState message="No geriatric-specific precautions documented" />
                  )}
                </TabsContent>
              </Tabs>
            </AccordionContent>
          </AccordionItem>

          {/* Side Effects */}
          <AccordionItem value="side-effects" className="border rounded-lg bg-card" id="side-effects">
            <AccordionTrigger className="px-4 sm:px-6 hover:no-underline hover:bg-muted/50 min-h-[56px]">
              <div className="flex items-center gap-2 sm:gap-3">
                <Activity className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" />
                <span className="font-semibold text-base sm:text-lg">
                  Side Effects
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
              <div className="mb-4">
                <Input
                  placeholder="Search side effects..."
                  value={sideEffectSearchQuery}
                  onChange={(e) => setSideEffectSearchQuery(e.target.value)}
                  className="max-w-xs"
                />
              </div>

              <Table>
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSideEffectSort('name')}
                    >
                      <div className="flex items-center gap-2">
                        <span>Side Effect</span>
                        <SortIcon column="name" currentColumn={sideEffectSortColumn} direction={sideEffectSortDirection} />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSideEffectSort('frequency')}
                    >
                      <div className="flex items-center gap-2">
                        <span>Frequency</span>
                        <SortIcon column="frequency" currentColumn={sideEffectSortColumn} direction={sideEffectSortDirection} />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSideEffectSort('severity')}
                    >
                      <div className="flex items-center gap-2">
                        <span>Severity</span>
                        <SortIcon column="severity" currentColumn={sideEffectSortColumn} direction={sideEffectSortDirection} />
                      </div>
                    </TableHead>
                    <TableHead>Note</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedSideEffects.length > 0 ? (
                    sortedSideEffects.map((effect, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{effect.name}</TableCell>
                        <TableCell className="text-sm">{effect.frequency}</TableCell>
                        <TableCell>
                          <SeverityIndicator severity={effect.severity} size="sm" />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {effect.note || '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="py-8">
                        <div className="flex justify-center items-center w-full">
                          <EmptyState message="No side effects match your search" showIcon={true} />
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </AccordionContent>
          </AccordionItem>

          {/* Interactions */}
          <AccordionItem value="interactions" className="border rounded-lg bg-card" id="interactions">
            <AccordionTrigger className="px-4 sm:px-6 hover:no-underline hover:bg-muted/50 min-h-[56px]">
              <div className="flex items-center gap-2 sm:gap-3">
                <GitBranch className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" />
                <span className="font-semibold text-base sm:text-lg">
                  Interactions
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
              <div className="mb-4">
                <Input
                  placeholder="Search interactions..."
                  value={interactionSearchQuery}
                  onChange={(e) => setInteractionSearchQuery(e.target.value)}
                  className="max-w-xs"
                />
              </div>

              <div className="overflow-hidden border border-border rounded-lg sm:rounded-md">
                <Table className="table-fixed w-full">
                  <TableHeader className="sticky top-0 bg-background">
                    <TableRow>
                      <TableHead 
                        className="w-20 cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => handleInteractionSort('type')}
                      >
                        <div className="flex items-center gap-2">
                          <span>Type</span>
                          <SortIcon column="type" currentColumn={interactionSortColumn} direction={interactionSortDirection} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="w-[25%] cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => handleInteractionSort('with')}
                      >
                        <div className="flex items-center gap-2">
                          <span>Interacts With</span>
                          <SortIcon column="with" currentColumn={interactionSortColumn} direction={interactionSortDirection} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="w-[35%] cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => handleInteractionSort('clinicalEffect')}
                      >
                        <div className="flex items-center gap-2">
                          <span>Clinical Effect</span>
                          <SortIcon column="clinicalEffect" currentColumn={interactionSortColumn} direction={interactionSortDirection} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="w-24 cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => handleInteractionSort('severity')}
                      >
                        <div className="flex items-center gap-2">
                          <span>Severity</span>
                          <SortIcon column="severity" currentColumn={interactionSortColumn} direction={interactionSortDirection} />
                        </div>
                      </TableHead>
                      <TableHead className="w-16 text-center">Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedInteractions.map((interaction, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="w-20">
                          <Badge variant="outline" className="text-xs px-2 py-0.5">
                            {interaction.type}
                          </Badge>
                        </TableCell>
                        <TableCell className="w-[25%] font-medium">
                          {interaction.with}
                        </TableCell>
                        <TableCell className="w-[35%] text-sm" style={{ whiteSpace: 'normal' }}>
                          {interaction.clinicalEffect}
                        </TableCell>
                        <TableCell className="w-24">
                          <SeverityIndicator severity={interaction.severity} size="sm" />
                        </TableCell>
                        <TableCell className="w-16 text-center">
                          {interaction.monographId && interaction.monographId > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleViewMonograph(
                                  interaction.monographType,
                                  interaction.monographId
                                )
                              }
                              className="h-8 w-8 p-0"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {sortedInteractions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8">
                          <EmptyState message="No interactions match your search" />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Drug-Disease Contraindications */}
          <AccordionItem value="disease-contraindications" className="border rounded-lg bg-card" id="disease-contraindications">
            <AccordionTrigger className="px-4 sm:px-6 hover:no-underline hover:bg-muted/50 min-h-[56px]">
              <div className="flex items-center gap-2 sm:gap-3">
                <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" />
                <span className="font-semibold text-base sm:text-lg">
                  Drug-Disease Contraindications
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Condition</TableHead>
                    <TableHead>Severity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drugData.drugDiseaseContraindications?.length > 0 ? (
                    drugData.drugDiseaseContraindications.map((contra, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{contra.condition}</TableCell>
                        <TableCell>
                          <SeverityIndicator severity={contra.severity} size="sm" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center py-8">
                        <EmptyState message="No disease-specific contraindications documented" />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </AccordionContent>
          </AccordionItem>

          {/* Therapeutic Alternatives */}
          <AccordionItem value="alternatives" className="border rounded-lg bg-card" id="alternatives">
            <AccordionTrigger className="px-4 sm:px-6 hover:no-underline hover:bg-muted/50 min-h-[56px]">
              <div className="flex items-center gap-2 sm:gap-3">
                <Repeat className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" />
                <span className="font-semibold text-base sm:text-lg">
                  Therapeutic Alternatives
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Drug Name</TableHead>
                    <TableHead>Drug Class</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drugData.alternatives?.length > 0 ? (
                    drugData.alternatives.map((alt, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{alt.name}</TableCell>
                        <TableCell className="text-sm">{alt.subGroup || '—'}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} className="py-8">
                        <div className="flex justify-center items-center w-full">
                          <EmptyState message="No therapeutic alternatives identified in our database" showIcon={true} />
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </AccordionContent>
          </AccordionItem>

          {/* Ingredients */}
          <AccordionItem value="ingredients" className="border rounded-lg bg-card" id="ingredients">
            <AccordionTrigger className="px-4 sm:px-6 hover:no-underline hover:bg-muted/50 min-h-[56px]">
              <div className="flex items-center gap-2 sm:gap-3">
                <Beaker className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" />
                <span className="font-semibold text-base sm:text-lg">
                  Ingredients
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
              <div className="space-y-3 mt-2">
                {/* Active Ingredients */}
                {drugData.ingredients
                  ?.filter((i) => i.type === 'Active')
                  .map((ingredient, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <Pill className="h-4 w-4 text-primary shrink-0" />
                        <span className="text-sm font-medium">{ingredient.name}</span>
                      </div>
                      <span className="text-xs text-green-600 font-semibold shrink-0">
                        Active
                      </span>
                    </div>
                  ))}
                
                {/* Inactive Ingredients */}
                {drugData.ingredients
                  ?.filter((i) => i.type === 'Inactive')
                  .map((ingredient, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="h-2 w-2 rounded-full bg-muted-foreground shrink-0" />
                        <span className="text-sm">{ingredient.name}</span>
                      </div>
                      <span className="text-xs text-muted-foreground font-medium shrink-0">
                        Inactive
                      </span>
                    </div>
                  ))}
                
                {!drugData.ingredients?.length && (
                  <EmptyState message="Ingredient information not provided by manufacturer" />
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Patient Education */}
          <AccordionItem value="patient-education" className="border rounded-lg bg-card" id="patient-education">
            <AccordionTrigger className="px-4 sm:px-6 hover:no-underline hover:bg-muted/50 min-h-[56px]">
              <div className="flex items-center gap-2 sm:gap-3">
                <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" />
                <span className="font-semibold text-base sm:text-lg">
                  Patient Information
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
              {drugData.patientEducation ? (
                <PatientEducation patientInfo={drugData.patientEducation} />
              ) : (
                <EmptyState message="Patient education information not available from source" />
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      {/* Monograph Modal */}
      {monographDialogOpen && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setMonographDialogOpen(false)}
        >
          <div 
            className="bg-background rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
              <h2 className="text-lg font-semibold">{monographTitle}</h2>
              <button
                onClick={() => setMonographDialogOpen(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-6">
              {monographLoading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                  <p className="text-sm text-muted-foreground">Loading monograph...</p>
                </div>
              ) : activeMonograph ? (
                <div className="space-y-6">
                  {activeMonograph.sections
                    ?.sort((a, b) => a.sequence - b.sequence)
                    .map((section, idx) => (
                      <div key={idx} className="space-y-2">
                        {section.title && (
                          <h3 className="font-semibold text-base">{section.title}</h3>
                        )}
                        {section.text && (
                          <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                            {section.text}
                          </p>
                        )}
                      </div>
                    ))}

                  {activeMonograph.references && activeMonograph.references.length > 0 && (
                    <CollapsibleReferences references={activeMonograph.references} />
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">Detailed information not available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Collapsible References Component
const CollapsibleReferences = ({ references }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-t pt-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 font-semibold text-base hover:underline"
      >
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
        References ({references.length})
      </button>
      {isOpen && (
        <div className="mt-4 space-y-3">
          {references
            .sort((a, b) => a.sequence - b.sequence)
            .map((ref, idx) => (
              <div key={idx} className="text-xs text-muted-foreground pl-6">
                <span className="font-semibold">[{idx + 1}]</span> {ref.text}
                {ref.category && (
                  <span className="ml-2 text-xs italic">({ref.category})</span>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

DrugDetailsTab.propTypes = {
  drugMedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  drugName: PropTypes.string,
};

export default DrugDetailsTab;
